
export interface IVueState {
    route: string;
    name: string;
    page: number;
    visited: boolean;
    error: boolean;
}

export interface IVueStateCollection {
    formType: string;
    formName: string;
    isRenewal: boolean;
    views: IVueState[];
}

export class FormBuilder {

    getFormHeader(formName: string): string {
        if (formName === "CCDDApplication") {
            return "Clean Construction & Demolition Debris Application";
        }
        if (formName === "RecyclingFacilityApplication") {
            return "Recycling Facility Application";
        }

        if (formName === "CCDDActivities") {
            return "Clean Construction & Demolition Debris Activities Report";
        }
        if (formName === "LiquidHazardousWaste") {
            return "Liquid Hazardous Waste Report";
        }
        if (formName === "NonResidentialHauler") {
            return "Non-Residential Hauler Report";
        }
        if (formName === "RecyclingActivities") {
            return "Recycling Activities Report";
        }
        if (formName === "ResidentialHauler") {
            return "Residential Hauler Report";
        }
        if (formName === "TransferStation") {
            return "Transfer Station Report";
        }
        return "";
    }

    getIndexByVueState(views: IVueState[], routeName: string): number {
        return views.map(item => item.route).indexOf(routeName);
    }

    getFormDescription(formType: string, formName: string): string {
        if (formType.toLowerCase() === "permits") {
            return "";
        }
        if (formType.toLowerCase() === "reporting") {
            if (formName.toLowerCase() === "ccddactivities") {
                return "As required under the provisions of the Environmental Control Ordinance (chapter 30 of the municipal code of Cook County): any owner or operator of a Clean Construction and Demolition Debris fill operation shall submit a report indicating the weight or volume of all materials collected.";
            } else if (formName.toLowerCase() === "liquidhazardouswaste") {
                return "The Liquid Hazardous Waste Ordinance was passed by the Cook County Board of Commissioners on December 16, 2015. The ordinance took effect January 1, 2016 and requires facilities in suburban Cook County to submit their annual liquid hazardous waste generation and corresponding fee on April 1 of each year. Pursuant to Section 5/3.475 of the Illinois Environmental Protection Act as \"special waste\" AND transported off-site under manifest according to the Federal Resource Conservation and Recovery Act regulations. Does not include medical waste, used oil, or cooking oil.";
            } else if (formName.toLowerCase() === "recyclingactivities") {
                return "As required under the provisions of the Environmental Control Ordinance (chapter 30 of the municipal code of Cook County): Any owner or operator of a Recycling Facility shall submit a report summarizing the recycling activities of the facility.";
            } else if (formName.toLowerCase() === "nonresidentialhauler" || formName.toLowerCase() === "residentialhauler") {
                return "As required under the provisions of the Environmental Control Ordinance (chapter 30 of the municipal code of Cook County): Any waste and recycling hauler operating within the boundaries of Cook County, except within the corporate limits of the City of Chicago, shall submit quarterly reports.";
            } else if (formName.toLowerCase() === "transferstation") {
                return "As required under the provisions of the Environmental Control Ordinance (chapter 30 of the municipal code of Cook County): Any owner or operator of a municipal solid waste (MSW) transfer station shall submit a quarterly report specifying the quantities of waste and/or recyclable materials accepted for transfer by the MSW transfer station. This report is required for MSW transfer stations located in the boundaries of Cook County, except within the corporate limits of the City of Chicago. The report must be completed in its entirety with the appropriate payment.";
            }
        }
        return "";
    }


    getViewStates(formType: string, formName: string, isRenewal: boolean): IVueStateCollection {
        if (formType.toLowerCase() === "permits") {
            if (formName === "RecyclingFacilityApplication") {
                if (isRenewal == true) {
                    return this.getRecyclingFacilityApplciationRenewalVueStates(formType, formName);
                } else {
                    return this.getRecyclingFacilityApplicationVueStates(formType, formName);
                }
            }
            if (formName === "CCDDApplication") {
                if (isRenewal == true) {
                    return this.getCCDDApplicationRenewalVueStates(formType, formName);
                } else {
                    return this.getCCDDApplicationVueStates(formType, formName);
                }
            }
            if (formName === "RockCrushingApplication") {
                return this.getRockCrushingApplicationVueStates(formType, formName);
            }
        }

        if (formType.toLowerCase() === "reporting") {
            return this.getReportingVueStates(formType, formName);
        }
        return { formType: "", formName: "", views: [], isRenewal: false};
    }

    getFormDefault(): any {
        return {
            formId: "",
            accountNumber: "",
            applicationNumber: "",
            applicationDate: new Date(),
            email: "",
            firstName: "",
            lastName: "",
            fees: []
        }
    }
    
    //Permits/CCDDApplication
    private getCCDDApplicationVueStates(formType: string, formName: string): IVueStateCollection {
        return {
            formType: formType,
            formName: formName,
            isRenewal: false,
            views: [
                { route: "ContactEmail", name: "Contact", page: 1, visited: false, error: false },
                { route: "SiteInfo", name: "Site Information", page: 2, visited: false, error: false },
                { route: "AcceptedMaterials", name: "Accepted Materials", page: 3, visited: false, error: false },
                { route: "OperatingHours", name: "Operating Hours", page: 4, visited: false, error: false },
                { route: "DailyOperations", name: "Daily Operations", page: 5, visited: false, error: false },
                { route: "Violations", name: "Violations", page: 6, visited: false, error: false },
                { route: "Signature", name: "Signature", page: 7, visited: false, error: false }
            ]
        }
    }

    private getCCDDApplicationRenewalVueStates(formType: string, formName: string): IVueStateCollection {
        return {
            formType: formType,
            formName: formName,
            isRenewal: true,
            views: [
                { route: "ContactEmail", name: "Contact", page: 1, visited: false, error: false },
                { route: "SiteInfoRenewal", name: "Site Information", page: 2, visited: false, error: false },
                { route: "AcceptedMaterials", name: "Accepted Materials", page: 3, visited: false, error: false },
                { route: "OperatingHours", name: "Operating Hours", page: 4, visited: false, error: false },
                { route: "DailyOperations", name: "Daily Operations", page: 5, visited: false, error: false },
                { route: "Violations", name: "Violations", page: 6, visited: false, error: false },
                { route: "Signature", name: "Signature", page: 7, visited: false, error: false }
            ]
        }
    }

    //Permits/RecyclingFacilityApplication
    private getRecyclingFacilityApplicationVueStates(formType: string, formName: string): IVueStateCollection {
        return {
            formType: formType,
            formName: formName,
            isRenewal: false,
            views: [
                { route: "ContactEmail", name: "Contact", page: 1, visited: false, error: false },
                { route: "SiteInfo", name: "Site Information", page: 2, visited: false, error: false },
                { route: "AcceptedMaterials", name: "Accepted Materials", page: 3, visited: false, error: false },
                { route: "OperatingHours", name: "Operating Hours", page: 4, visited: false, error: false },
                { route: "DailyOperations", name: "Daily Operations", page: 5, visited: false, error: false },
                { route: "LiquidHazardousWaste", name: "Liquid Hazardous Waste", page: 6, visited: false, error: false },
                { route: "Violations", name: "Violations", page: 7, visited: false, error: false },
                { route: "Signature", name: "Signature", page: 8, visited: false, error: false }
            ]
        }
    }

    private getRecyclingFacilityApplciationRenewalVueStates(formType: string, formName: string): IVueStateCollection {
        return {
            formType: formType,
            formName: formName,
            isRenewal: true,
            views: [
                { route: "ContactEmail", name: "Contact", page: 1, visited: false, error: false },
                { route: "SiteInfoRenewal", name: "Site Information", page: 2, visited: false, error: false },
                { route: "AcceptedMaterials", name: "Accepted Materials", page: 3, visited: false, error: false },
                { route: "OperatingHours", name: "Operating Hours", page: 4, visited: false, error: false },
                { route: "DailyOperations", name: "Daily Operations", page: 5, visited: false, error: false },
                { route: "LiquidHazardousWaste", name: "Liquid Hazardous Waste", page: 6, visited: false, error: false },
                { route: "Violations", name: "Violations", page: 7, visited: false, error: false },
                { route: "Signature", name: "Signature", page: 8, visited: false, error: false }
            ]
        }
    }

    //Reporting/CCDDActivities
    //Reporting/LiquidHazardousWaste
    //Reporting/NonResidentialHauler
    //Reporting/RecyclingActivities
    //Reporting/ResidentialHauler
    //Reporting/TransferStation
    private getReportingVueStates(formType: string, formName: string): IVueStateCollection {
        //Note: these are only named the same, but the underlying components are different.
        return {
            formType: formType,
            formName: formName,
            isRenewal: false,
            views: [
                { route: "FacilityInfo", name: "Facility Information", page: 1, visited: false, error: false },
                { route: "Materials", name: "Total Materials Collected", page: 2, visited: false, error: false },
                { route: "Certification", name: "Certification / Signature", page: 3, visited: false, error: false }
            ]
        }
    }

    //Permits/RockCrushingApplication
    private getRockCrushingApplicationVueStates(formType: string, formName: string): IVueStateCollection {
        return {
            formType: formType,
            formName: formName,
            isRenewal: false,
            views: [
                {route: "ContactEmail", name: "Contact", page: 1, visited: false, error: false},
                {route: "SiteInfo", name: "Project Information", page: 2, visited: false, error: false},
                {route: "AcceptedMaterials", name: "Materials Management", page: 3, visited: false, error: false},
                {route: "Signature", name: "Signature", page: 4, visited: false, error: false}
            ]
        }
    }
}

export default new FormBuilder();
